import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

const ESTIMATE = 76;
const OVERSCAN = 6;

interface Props<T> {
  items: T[];
  getKey: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
}

export default function VirtualTaskRows<T>({ items, getKey, renderItem }: Props<T>) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const heightsRef = useRef<Map<string, number>>(new Map());
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(520);
  const [version, setVersion] = useState(0);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => setViewport(el.clientHeight || 520);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const root = scrollerRef.current;
    if (!root) return;
    let changed = false;
    root.querySelectorAll<HTMLElement>('[data-task-key]').forEach(node => {
      const key = node.dataset.taskKey;
      if (!key) return;
      const height = node.offsetHeight;
      const previous = heightsRef.current.get(key) ?? 0;
      if (Math.abs(previous - height) >= 1) {
        heightsRef.current.set(key, height);
        changed = true;
      }
    });
    if (changed) setVersion(current => current + 1);
  });

  const offsets = new Array<number>(items.length + 1);
  offsets[0] = 0;
  for (let index = 0; index < items.length; index += 1) {
    offsets[index + 1] = offsets[index] + (heightsRef.current.get(getKey(items[index])) ?? ESTIMATE);
  }
  const total = offsets[items.length] ?? 0;

  let start = 0;
  while (start < items.length && offsets[start + 1] < scrollTop) start += 1;
  start = Math.max(0, start - OVERSCAN);

  let end = start;
  const limit = scrollTop + viewport;
  while (end < items.length && offsets[end] < limit) end += 1;
  end = Math.min(items.length, end + OVERSCAN);

  return (
    <Box
      ref={scrollerRef}
      onScroll={event => setScrollTop(event.currentTarget.scrollTop)}
      sx={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}
    >
      <Box sx={{ height: total, position: 'relative' }} data-version={version}>
        {items.slice(start, end).map((item, index) => {
          const absolute = start + index;
          const key = getKey(item);
          return (
            <Box
              key={key}
              data-task-key={key}
              sx={{ position: 'absolute', top: offsets[absolute], left: 0, right: 0 }}
            >
              {renderItem(item)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
